import { useState } from 'react';
import { RULE_EDITOR_TOGGLE_LABEL } from '../terms';
import { Button, Cell, type Column, ErrorText, Lines, Row, Subsection, Table } from '../ui';
import { signalHelpRows } from './signals';
import { useRuleFile } from './useRuleFile';

/**
 * 규칙 파일 편집 (24회차, 제안 9).
 *
 * 지금까지 규칙은 관제 서버에 들어가 파일을 고쳐야 바꿀 수 있었다. 임계 값은 운영하면서
 * 계속 바뀌는 값인데 그때마다 서버에 들어가는 것은 현실적이지 않다.
 *
 * 고치는 것은 **파일 전문**이다. 규칙을 표에서 한 줄씩 편집하게 만들면 주석이 사라진다.
 * 규칙 파일의 주석은 "왜 이 값인가"를 적어 둔 자리라 그것을 잃으면 다음 사람이 못 읽는다.
 *
 * 단추는 둘이다. **검증**은 파일을 건드리지 않고 이 내용으로 규칙을 세울 수 있는지만 보고,
 * **적용**은 검증을 통과했을 때만 파일에 적고 그 자리에서 감시에 반영한다. 서버가 직전
 * 파일을 한 벌 남기므로 잘못 적용해도 되돌릴 수 있다.
 *
 * 52회차부터 관리자 계정만 규칙을 바꾼다(일반 계정에게는 이 구획이 없고 서버도 403을 준다).
 * 그 한 줄을 구획 안에 적어 둔다(토큰 관리 화면과 같다).
 */

const HELP_COLUMNS: readonly Column[] = [
  { label: '값 이름', width: 'w-40' },
  { label: '무엇을 보나' },
  { label: '조건 칸', width: 'w-44' },
  { label: '단위', width: 'w-16' },
  { label: '예시', width: 'w-64' },
];

export function RuleEditor({
  signals,
  onApplied,
  expanded,
  onExpandedChange,
}: {
  signals: readonly string[];
  /** 적용에 성공했을 때. 위의 규칙 표·통계가 같은 시점에 다시 읽는다 (24회차 리뷰 10) */
  onApplied?: () => void;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
}) {
  const rules = useRuleFile(onApplied);
  const [help, setHelp] = useState(false);
  const changed = rules.text !== rules.saved;
  const result = rules.result;

  return (
    <div className="space-y-4">
      <Subsection
        title="규칙 파일 편집"
        note={[
          '파일 내용을 그대로 고친다.',
          '적용하면 재시작 없이 반영되고 직전 파일은 서버에 한 벌 남는다',
        ]}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {expanded ? (
              <>
                <Button size="sm" onClick={rules.reload} disabled={rules.loading || rules.busy}>
                  {rules.loading ? '읽는 중…' : '다시 읽기'}
                </Button>
                <Button size="sm" onClick={rules.revert} disabled={!changed || rules.busy}>
                  되돌리기
                </Button>
                <Button
                  size="sm"
                  onClick={() => void rules.check()}
                  disabled={rules.busy || !rules.readable}
                >
                  검증
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => void rules.apply()}
                  disabled={rules.busy || !rules.readable || !changed}
                  title={changed ? undefined : '고친 것이 없다'}
                >
                  {rules.busy ? '보내는 중…' : '적용'}
                </Button>
              </>
            ) : null}
            <Button size="sm" onClick={() => onExpandedChange(!expanded)} ariaExpanded={expanded}>
              {expanded ? RULE_EDITOR_TOGGLE_LABEL.collapse : RULE_EDITOR_TOGGLE_LABEL.expand}
            </Button>
          </div>
        }
      >
        <p className="text-xs text-muted">
          <Lines
            lines={[
              '관리자 계정만 규칙을 바꾼다.',
              rules.file === '' ? '규칙 파일 경로가 설정돼 있지 않다' : `파일 ${rules.file}`,
            ]}
          />
        </p>

        {expanded ? (
          <>
            {rules.error !== undefined ? <ErrorText>{rules.error}</ErrorText> : null}

            {rules.readable || rules.loading ? (
              <textarea
                id="alerts-rule-text"
                aria-label="규칙 파일"
                spellCheck={false}
                value={rules.text}
                onChange={(event) => rules.setText(event.target.value)}
                className="tabular h-96 w-full resize-y rounded-xl border border-line bg-panel p-3 font-mono text-xs leading-5 text-ink"
              />
            ) : (
              <p className="text-sm text-fail">
                <Lines
                  lines={[
                    '규칙 파일을 읽지 못해 편집할 수 없다.',
                    '관제 서버의 APM_ALERT_RULES 설정과 파일 권한을 볼 것',
                  ]}
                />
              </p>
            )}

            {result === undefined ? null : (
              <div
                className={`rounded-xl border px-4 py-3 text-sm ${
                  result.ok ? 'border-hair bg-panel' : 'border-fail/40 bg-fail/5'
                }`}
              >
                <p className={result.ok ? 'font-medium text-ink' : 'font-medium text-fail'}>
                  {headline(result)}
                </p>
                {result.error === undefined ? null : (
                  <p className="mt-1 break-words text-sm text-fail">{result.error}</p>
                )}
                {result.problems.length === 0 ? null : (
                  <ul className="mt-2 space-y-1 text-sm text-fail">
                    {result.problems.map((problem) => (
                      <li key={problem} className="break-words">
                        {problem}
                      </li>
                    ))}
                  </ul>
                )}
                {result.saved && result.backup !== undefined ? (
                  <p className="mt-2 text-xs text-muted">직전 파일 {result.backup}</p>
                ) : null}
              </div>
            )}
          </>
        ) : null}
      </Subsection>

      {expanded ? (
        <Subsection
          title="쓸 수 있는 값"
          note={[
            '규칙 하나는 값 하나를 본다. 조건 칸 이름과 예시는 파일에 그대로 적는 글자다.',
            'match는 service·txType 목록으로 이 규칙이 볼 대상만 고른다. keyBy는 판정 상태를 쪼갤 기준이며 문자열 하나나 목록으로 적는다',
          ]}
          actions={
            <Button size="sm" onClick={() => setHelp(!help)} ariaExpanded={help}>
              {help ? '접기' : '펼치기'}
            </Button>
          }
        >
          {help ? (
            <Table columns={HELP_COLUMNS} caption="규칙에 쓸 수 있는 값" minWidth="min-w-[860px]">
              {signalHelpRows(signals).map((row) => (
                <Row key={row.signal}>
                  <Cell mono nowrap>
                    {row.signal}
                  </Cell>
                  <Cell className="break-words">
                    {row.what}
                    <div className="text-muted">쪼갤 기준 {row.keyBy}</div>
                  </Cell>
                  <Cell mono tone="muted" className="break-words">
                    {row.condition}
                  </Cell>
                  <Cell tone="muted" nowrap>
                    {row.unit}
                  </Cell>
                  <Cell mono tone="muted" className="break-words">
                    {row.example}
                  </Cell>
                </Row>
              ))}
            </Table>
          ) : null}
        </Subsection>
      ) : null}
    </div>
  );
}

/** 결과 한 줄. 적용 전후 규칙 수를 나란히 적는다 */
export function headline(result: {
  ok: boolean;
  saved: boolean;
  rules: number;
  channels: number;
  before: number;
}): string {
  if (!result.ok) return '적용하지 않았다. 파일 모양이 규칙이 아니다';
  const counts = `규칙 ${result.before}개 → ${result.rules}개, 채널 ${result.channels}개`;
  return result.saved ? `적용했다. ${counts}` : `쓸 수 있다. 적용하면 ${counts}`;
}
